import React, { useEffect, useRef, useState } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import * as XLSX from "xlsx";
import {
  useAddDoctorsMasterData,
  useDoctorMasterDataWithoutPagination,
  useEditDoctorsMasterData,
} from "../../hooks/useDoctorMasterData";
import { toast, ToastContainer } from "react-toastify";
import cityService from "../../services/cityService";
import countryService from "../../services/countryService";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

export default function ImportHCP() {
  const { mutate: addHCP } = useAddDoctorsMasterData();
  const { mutate: editHCP } = useEditDoctorsMasterData();
  const { data: existingHCPs } = useDoctorMasterDataWithoutPagination();

  const fileInputRef = useRef(null);

  // State for progress tracking
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    chunk: 0,
    totalChunks: 0,
    totalImported: 0,
    totalUpdated: 0,
    totalSkipped: 0
  });

  // Helper function to chunk array into smaller pieces
  const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  // Helper function to delay execution
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Process a single chunk of HCP data
  const processChunk = async (chunk, chunkIndex, totalChunks, onProgressUpdate, baseRowCount) => {
    const processedNationalIds = new Set();
    const processedAPONumbers = new Set();
    let successCount = 0;
    let skippedCount = 0;
    let updatedCount = 0;
    let currentRowInChunk = 0;

    for (const row of chunk) {
      currentRowInChunk++;
      const currentRow = baseRowCount + currentRowInChunk;
      // Validate required fields (excluding city and country)
      if (
        !row["First Name"] ||
        !row["Family Name"] ||
        !row["Email"] ||
        !row["National ID"] ||
        !row["APO Number"]
      ) {
        // console.error(`Missing required fields in row: ${row["First Name"]} ${row["Family Name"]} ${row["Email"]} ${row["National ID"]} ${row["APO Number"]}`);
        toast.error(
          `Missing required fields in row: ${row["First Name"]} ${row["Family Name"]} ${row["Email"]} ${row["National ID"]} ${row["APO Number"]}`
        );
        skippedCount++;
        // Update progress in real-time
        if (onProgressUpdate) {
          onProgressUpdate({
            type: 'skipped',
            currentRow: currentRow
          });
        }
        continue;
      }

      try {
        const nationalId = row["National ID"].toString();
        const apoNumber = row["APO Number"] || "N/A";

        // Check for existing HCP by National ID or APO Number
        const existingHCP = existingHCPs?.find(
          (hcp) => hcp.national_id === nationalId
        );

        let cityId = null;
        let countryId = null;

        // Try to get city ID if provided and not "unknown"
        if (
          row["City"]?.trim() &&
          row["City"].toString().trim().toLowerCase() !== "unknown"
        ) {
          const cityName = row["City"].toString().trim().toLowerCase();
          try {
            cityId = await cityService.getIdByName(cityName);
          } catch (error) {
            // console.warn(`Could not find city: ${cityName}, ${row["First Name"]} ${row["Family Name"]} ${row["Email"]} `);
            toast.error(`Could not find city: ${cityName}, ${row["First Name"]} ${row["Family Name"]} ${row["Email"]} `);
          }
        }

        // Try to get country ID if provided
        if (row["Country"]?.trim()) {
          const countryName = row["Country"]
            .toString()
            .trim()
            .toLowerCase();
          try {
            countryId = await countryService.getIdByName(countryName);
          } catch (error) {
            // console.warn(`Could not find country: ${countryName} for ${row["First Name"]} ${row["Family Name"]} ${row["Email"]} `);
            toast.error(`Could not find country: ${countryName} for ${row["First Name"]} ${row["Family Name"]} ${row["Email"]} `);
          }
        }

        // Clean phone number - handle both string and numeric values
        const phoneNumber = row["Phone Number"]
          ? typeof row["Phone Number"] === "string"
            ? row["Phone Number"].trim().replace(/\D/g, "")
            : row["Phone Number"].toString().replace(/\D/g, "")
          : "N/A";

        // Clean organization name - handle the extra space in the key
        const organization =
          row["Organization "]?.trim() ||
          row["Organization"]?.trim() ||
          "N/A";

        const newHcpData = {
          surname: row["Surname"]?.toString().trim() || "N/A",
          first_name: row["First Name"]?.toString().trim() || "",
          family_name: row["Family Name"]?.toString().trim() || "",
          email: row["Email"]?.toString().trim() || "",
          phone_number: phoneNumber,
          specialty: row["Specialty"]?.toString().trim() || "N/A",
          industry_area: row["Industry Area"]?.toString().trim() || "N/A",
          medical_license:
            row["Medical License"]?.toString().trim() || "N/A",
          organization: organization,
          national_id: nationalId,
          APO_number: apoNumber,
          city_id: cityId || "N/A",
          country_id: countryId || "N/A",
          title: row["Title"]?.toString().trim() || "N/A",
        };

        if (existingHCP) {
          // Check if there are any changes in the data
          const hasChanges = Object.keys(newHcpData).some((key) => {
            const newValue = newHcpData[key];
            const oldValue = existingHCP[key];

            // Skip comparison for certain fields
            if (key === "city_id" || key === "country_id") {
              return false;
            }

            // Don't update if new value is 'N/A' and old value exists
            if (newValue === "N/A" && oldValue && oldValue !== "N/A") {
              return false;
            }

            // Compare values
            return newValue !== oldValue;
          });

          if (hasChanges) {
            // Update existing HCP
            editHCP({
              id: existingHCP.id,
              data: newHcpData,
            });
            updatedCount++;
            // Update progress in real-time
            if (onProgressUpdate) {
              onProgressUpdate({
                type: 'updated',
                currentRow: currentRow
              });
            }
          } else {
            skippedCount++;
            // Update progress in real-time
            if (onProgressUpdate) {
              onProgressUpdate({
                type: 'skipped',
                currentRow: currentRow
              });
            }
          }
        } else {
          // Check for duplicates in the import file
          if (processedNationalIds.has(nationalId)) {
            skippedCount++;
            // Update progress in real-time
            if (onProgressUpdate) {
              onProgressUpdate({
                type: 'skipped',
                currentRow: (chunkIndex * chunk.length) + currentRowInChunk
              });
            }
            continue;
          }

          if (apoNumber !== "N/A" && processedAPONumbers.has(apoNumber)) {
            skippedCount++;
            // Update progress in real-time
            if (onProgressUpdate) {
              onProgressUpdate({
                type: 'skipped',
                currentRow: (chunkIndex * chunk.length) + currentRowInChunk
              });
            }
            continue;
          }

          // Add new HCP
          addHCP({
            ...newHcpData,
            created_at: new Date().toISOString(),
          });
          processedNationalIds.add(nationalId);
          if (apoNumber !== "N/A") {
            processedAPONumbers.add(apoNumber);
          }
          successCount++;
          // Update progress in real-time
          if (onProgressUpdate) {
            onProgressUpdate({
              type: 'imported',
              currentRow: currentRow
            });
          }
        }
      } catch (error) {
        toast.error(`Error processing row: ${JSON.stringify(row)} for ${row["First Name"]} ${row["Family Name"]} ${row["Email"]} `);
        console.error("Error processing row:", row, error);
        skippedCount++;
        // Update progress in real-time for error case
        if (onProgressUpdate) {
          onProgressUpdate({
            type: 'skipped',
            currentRow: currentRow
          });
        }
      }
    }

    return { successCount, skippedCount, updatedCount };
  };

  // Process all chunks sequentially
  const processAllChunks = async (chunks, totalRows) => {
    let totalSuccess = 0;
    let totalSkipped = 0;
    let totalUpdated = 0;
    let baseRowCount = 0; // Track rows processed before current chunk

    // Create progress update callback
    const updateProgress = (updateInfo) => {
      setProgress(prev => {
        let newImported = prev.totalImported;
        let newUpdated = prev.totalUpdated;
        let newSkipped = prev.totalSkipped;

        if (updateInfo.type === 'imported') {
          newImported++;
        } else if (updateInfo.type === 'updated') {
          newUpdated++;
        } else if (updateInfo.type === 'skipped') {
          newSkipped++;
        }

        // Determine current chunk based on row count
        let currentChunkNum = 0;
        for (let i = 0; i < chunks.length; i++) {
          if (updateInfo.currentRow <= chunks.slice(0, i + 1).reduce((sum, c) => sum + c.length, 0)) {
            currentChunkNum = i + 1;
            break;
          }
        }

        return {
          current: updateInfo.currentRow,
          total: totalRows,
          chunk: currentChunkNum,
          totalChunks: chunks.length,
          totalImported: newImported,
          totalUpdated: newUpdated,
          totalSkipped: newSkipped
        };
      });
    };

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];

      // Calculate base row count (sum of all previous chunks)
      const baseRowCount = chunks.slice(0, i).reduce((sum, c) => sum + c.length, 0);

      // Process the chunk with progress callback
      const result = await processChunk(chunk, i, chunks.length, updateProgress, baseRowCount);

      // Accumulate results
      totalSuccess += result.successCount;
      totalSkipped += result.skippedCount;
      totalUpdated += result.updatedCount;

      // Log progress
      // console.log(`Processing chunk ${i + 1}/${chunks.length} (${chunk.length} rows)`);

      // Wait before processing next chunk (200ms delay)
      if (i < chunks.length - 1) {
        await delay(200);
      }
    }

    return { totalSuccess, totalSkipped, totalUpdated };
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    // Create template headers
    const headers = [
      "Surname",
      "First Name",
      "Family Name",
      "Email",
      "Phone Number",
      "Specialty",
      "Country",
      "City",
      "Industry Area",
      "Medical License",
      "Organization",
      "National ID",
      "APO Number",
      "Title",
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers]);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "HCP Template");

    // Save file
    XLSX.writeFile(wb, "create_hcp_template.xlsx");
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setIsProcessing(true);
        setProgress({
          current: 0,
          total: 0,
          chunk: 0,
          totalChunks: 0,
          totalImported: 0,
          totalUpdated: 0,
          totalSkipped: 0
        });

        const reader = new FileReader();
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Split data into chunks (400-500 rows per chunk)
          const chunkSize = 450; // Middle ground between 400-500
          const chunks = chunkArray(jsonData, chunkSize);

          // console.log(`Processing ${jsonData.length} rows in ${chunks.length} chunks of ${chunkSize} rows each`);

          // Set progress total immediately so the card shows up
          setProgress({
            current: 0,
            total: jsonData.length,
            chunk: 0,
            totalChunks: chunks.length,
            totalImported: 0,
            totalUpdated: 0,
            totalSkipped: 0
          });

          // Process all chunks sequentially
          const results = await processAllChunks(chunks, jsonData.length);

          // Show summary toast
          toast.info(
            `Import completed: ${results.totalSuccess} HCPs added, ${results.totalUpdated} updated, ${results.totalSkipped} skipped`
          );

          // Reset progress
          setProgress({
            current: 0,
            total: 0,
            chunk: 0,
            totalChunks: 0,
            totalImported: 0,
            totalUpdated: 0,
            totalSkipped: 0
          });
          setIsProcessing(false);
          event.target.value = "";
        };
        reader.readAsArrayBuffer(file);
      } catch (error) {
        // console.error("Error importing HCPs:", error);
        toast.error(error.message || "Error importing HCPs!");
        setIsProcessing(false);
        setProgress({
          current: 0,
          total: 0,
          chunk: 0,
          totalChunks: 0,
          totalImported: 0,
          totalUpdated: 0,
          totalSkipped: 0
        });
      }
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="space-y-4">
        {/* Progress Bar */}
        {isProcessing && progress.total > 0 && (
          <div className="bg-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Processing chunk {progress.chunk} of {progress.totalChunks}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((progress.current / progress.total) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-300 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {progress.current} of {progress.total} rows processed
            </div>
            <div className="mt-3 pt-3 border-t border-gray-300">
              <div className="text-xs font-medium text-gray-700 mb-1">Import Statistics:</div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="text-green-600">
                  <span className="font-medium">Added:</span> {progress.totalImported}
                </div>
                <div className="text-blue-600">
                  <span className="font-medium">Updated:</span> {progress.totalUpdated}
                </div>
                <div className="text-gray-600">
                  <span className="font-medium">Skipped:</span> {progress.totalSkipped}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Controls */}
        <div className="flex items-center space-x-2">
          <button
            className="bg-amber-500 hover:bg-amber-600 transition-colors duration-200 text-white rounded-md px-4 py-2 flex items-center gap-2"
            onClick={handleDownloadTemplate}
            disabled={isProcessing}
          >
            <FileDownloadIcon />
            Download Template
          </button>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx"
              onChange={handleFileChange}
              style={{ display: "none" }}
              disabled={isProcessing}
            />
            <button
              data-track="import-hcp-button"
              data-module="HCPs"
              type="button"
              onClick={handleClick}
              className={`transition-colors duration-200 text-white rounded-md px-4 py-2 flex items-center gap-2 ${isProcessing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                }`}
              disabled={isProcessing}
            >
              <FileUploadIcon />
              {isProcessing ? 'Processing...' : 'Import HCPs'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
